import React, { useState } from 'react';
import { FiX, FiFileText, FiCreditCard, FiShare2, FiDownload, FiLoader } from 'react-icons/fi';
import { shareBill, shareQRPayment, isFileShareSupported } from '../utils/shareUtils';
import { generateOrderCode } from '../utils/formatters';

const ShareModal = ({ order, shopSettings, onClose }) => {
  const [loading, setLoading] = useState(null); // 'bill' | 'qr' | null
  const [error, setError] = useState('');
  
  const canShareFiles = isFileShareSupported();
  const orderCode = generateOrderCode(order);
  const hasBank = shopSettings?.bankAccounts?.some(b => b.isDefault);

  const handleShareBill = async () => {
    setLoading('bill');
    setError('');
    
    try {
      const result = await shareBill(order, shopSettings);
      
      if (result.downloaded) {
        // File was downloaded instead of shared
        setError('');
      }
      
      if (!result.cancelled) {
        onClose();
      }
    } catch (err) {
      console.error('Share bill error:', err);
      setError('Không thể tạo hình ảnh hóa đơn. Vui lòng thử lại.');
    } finally {
      setLoading(null);
    }
  };

  const handleShareQR = async () => {
    if (!hasBank) {
      setError('Chưa cấu hình tài khoản ngân hàng mặc định');
      return;
    }
    
    setLoading('qr');
    setError('');
    
    try {
      const result = await shareQRPayment(order, shopSettings);
      
      if (result.downloaded) {
        setError('');
      }
      
      if (!result.cancelled) {
        onClose();
      }
    } catch (err) {
      console.error('Share QR error:', err);
      setError(err.message || 'Không thể tạo mã QR. Vui lòng thử lại.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FiShare2 className="text-green-600" size={20} />
            <h3 className="font-semibold text-gray-800">Chia sẻ đơn hàng</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FiX size={20} className="text-gray-500" />
          </button>
        </div>
        
        {/* Order Info */}
        <div className="px-4 py-3 bg-gray-50 border-b">
          <div className="text-sm text-gray-600">Mã đơn: <span className="font-semibold text-gray-800">{orderCode}</span></div>
          <div className="text-sm text-gray-600">Tổng tiền: <span className="font-semibold text-green-600">{order.totalAmount?.toLocaleString('vi-VN')}đ</span></div>
        </div>
        
        {/* Share Options */}
        <div className="p-4 space-y-3">
          {/* Share Bill */}
          <button
            onClick={handleShareBill}
            disabled={loading !== null}
            className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 rounded-xl border border-green-200 transition-all disabled:opacity-50"
          >
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
              {loading === 'bill' ? (
                <FiLoader className="text-white animate-spin" size={24} />
              ) : (
                <FiFileText className="text-white" size={24} />
              )}
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-gray-800">Share hóa đơn</div>
              <div className="text-sm text-gray-500">Chia sẻ hình ảnh hóa đơn chi tiết</div>
            </div>
            {canShareFiles ? (
              <FiShare2 className="text-green-500" size={20} />
            ) : (
              <FiDownload className="text-green-500" size={20} />
            )}
          </button>
          
          {/* Share QR */}
          <button
            onClick={handleShareQR}
            disabled={loading !== null || !hasBank}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all disabled:opacity-50 ${
              hasBank 
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-blue-200'
                : 'bg-gray-50 border-gray-200 cursor-not-allowed'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${hasBank ? 'bg-blue-500' : 'bg-gray-400'}`}>
              {loading === 'qr' ? (
                <FiLoader className="text-white animate-spin" size={24} />
              ) : (
                <FiCreditCard className="text-white" size={24} />
              )}
            </div>
            <div className="text-left flex-1">
              <div className="font-semibold text-gray-800">Share QR thanh toán</div>
              <div className="text-sm text-gray-500">
                {hasBank ? 'Chia sẻ mã QR chuyển khoản' : 'Chưa cấu hình ngân hàng'}
              </div>
            </div>
            {hasBank && (
              canShareFiles ? (
                <FiShare2 className="text-blue-500" size={20} />
              ) : (
                <FiDownload className="text-blue-500" size={20} />
              )
            )}
          </button>
        </div>
        
        {/* Error */}
        {error && (
          <div className="px-4 pb-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          </div>
        )}
        
        {/* Footer hint */}
        <div className="px-4 pb-4">
          <div className="text-xs text-center text-gray-400">
            {canShareFiles 
              ? 'Hình ảnh sẽ được chia sẻ qua Zalo, Messenger...'
              : 'Hình ảnh sẽ được tải về thiết bị của bạn'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
